import { supabase } from "./supabase";

export const IMPERSONATION_KEY = "portal-admin-workspace";

// Last-known API payloads survive reloads so returning users see their real
// numbers immediately instead of zeros while the live fetch is in flight.
const CACHE_PREFIX = "portal-cache:";

export function readCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

export function writeCache(key: string, value: unknown): void {
  try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value)); } catch { /* blocked or full storage never breaks the live path */ }
}

export function removeCache(key: string): void {
  try { localStorage.removeItem(CACHE_PREFIX + key); } catch { /* ignore */ }
}

// Impersonation must never read or write the admin's own cached numbers.
export function cacheScope(userId: string | undefined): string | null {
  if (!userId) return null;
  try { if (sessionStorage.getItem(IMPERSONATION_KEY)) return null; } catch { return null; }
  return userId.slice(0, 8);
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = supabase ? (await supabase.auth.getSession()).data.session : null;
  const impersonation = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(IMPERSONATION_KEY) : null;
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      ...(impersonation && !path.startsWith("/api/admin") ? { "X-Admin-Impersonate": impersonation } : {}),
      ...(init.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({})) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "The request could not be completed.");
  return payload;
}
